/**
 * Oeffentliche "Rechnungsadresse ergaenzen"-Seite (KEIN Login) — Ruecksprung-Weg fuer die
 * Rechnungsadresse-Pflicht (Antoine, 30.08.2026, sql/344). Muster wie pages/offer-public.tsx,
 * aber bewusst schlank: ein Formular, keine Angebots-/Zahlungslogik.
 *
 * Der bevorzugte Weg zur Adresse ist inzwischen der Stripe-Zahlungslink selbst
 * (billing_address_collection:'required') bzw. die Angebots-Annahme-Seite (offer-public.tsx) -
 * diese Seite ist die Rueckfall-Abdeckung fuer den Bestand und fuer Zahlungswege ohne
 * Stripe-Checkout (Ueberweisung, manuelles "bezahlt markieren").
 */
import { useEffect, useState } from "react";
import { anlassAnzeige } from "../ui/format";
import { useParams } from "react-router-dom";
import { Icon, Input, Button } from "../ui/components";
import { AdresseFelder, type AdresseFeld, type AdresseWerte } from "../ui/AdresseFelder";
import { LAENDER, LAENDER_EN } from "../lib/laender";
import { GastRahmen } from "../ui/BetriebsKopf";

/**
 * Punkt 41 (Folge): alle festen Texte der Seite in der Kommunikationssprache des Vorgangs
 * (`offerLanguage` aus der API) — dasselbe Muster wie `T`/`lang` in pages/offer-public.tsx.
 * Vor dem Laden (und bei einem ungueltigen Link) ist die Sprache unbekannt: dann Deutsch.
 */
const T = {
  de: {
    loading: "Lädt …",
    notFound: "Dieser Link ist nicht (mehr) gültig.",
    loadError: "Fehler beim Laden.",
    saveError: "Die Adresse konnte nicht gespeichert werden. Bitte erneut versuchen.",
    doneTitle: "Vielen Dank!",
    doneText: "Ihre Rechnungsadresse wurde gespeichert. Ihre Rechnung wird nun erstellt.",
    heading: "Rechnungsadresse ergänzen",
    introEvent: (occasion: string) => `Für Ihre Veranstaltung „${occasion}“ `,
    introInvoice: "Für Ihre Rechnung ",
    introRest: "benötigen wir noch Ihre vollständige Rechnungsadresse.",
    refPlaceholder: "Referenz für Ihre Buchhaltung (z. B. Veranstaltungs-/Bestellnummer, Kostenstelle) — optional",
    saving: "Wird gespeichert …",
    save: "Speichern",
    labels: { firma: "Firma (falls zutreffend)", addition: "Adresszusatz (optional)" },
  },
  en: {
    loading: "Loading …",
    notFound: "This link is no longer valid.",
    loadError: "Something went wrong while loading.",
    saveError: "We couldn't save your address. Please try again.",
    doneTitle: "Thank you!",
    doneText: "Your billing address has been saved. We will now create your invoice.",
    heading: "Complete your billing address",
    introEvent: (occasion: string) => `For your event “${occasion}”, `,
    introInvoice: "For your invoice, ",
    introRest: "we still need your full billing address.",
    refPlaceholder: "Reference for your accounting (e.g. event/order number, cost center) — optional",
    saving: "Saving …",
    save: "Save",
    labels: {
      firma: "Company (if applicable)",
      name: "Name",
      street: "Street and house number",
      addition: "Address line 2 (optional)",
      zip: "Postal code",
      city: "City",
      land: "Country",
    },
  },
} as const;

type Phase = "loading" | "ready" | "submitting" | "done" | "error";

interface RechnungsadresseData {
  occasion: string | null;
  kundenreferenz: string | null;
  name: string | null;
  company: string | null;
  addressStreet: string | null;
  addressZip: string | null;
  addressCity: string | null;
  addressAddition?: string | null;
  addressCountryCode?: string | null;
  /** Punkt 41 (Folge): Kommunikationssprache des Vorgangs; fehlt sie, gilt Deutsch. */
  offerLanguage?: string | null;
}

export default function PublicRechnungsadressePage() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);
  const [occasion, setOccasion] = useState<string | null>(null);
  // PR4 (Adressmodell, WARTESCHLANGE Punkt 7): die ganze Adresse ueber die gemeinsame
  // `AdresseFelder`-Komponente. Seit sql/485 auch Zusatz + Land (vorbefuellt, Land ohne
  // gespeicherten Wert vorausgewaehlt „DE“). Ein geleerter Zusatz wird mitgeschickt und leert ihn.
  const [adresse, setAdresse] = useState<AdresseWerte>({ company: "", name: "", street: "", addition: "", zip: "", city: "", countryCode: "" });
  const [kundenreferenz, setKundenreferenz] = useState("");
  const [lang, setLang] = useState<"de" | "en">("de");
  const t = T[lang];

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`/api/public/rechnungsadresse/${token}`);
        if (!res.ok) throw new Error(res.status === 404 ? T.de.notFound : T.de.loadError);
        const body = (await res.json()) as { data: RechnungsadresseData };
        setLang(body.data.offerLanguage === "en" ? "en" : "de");
        setOccasion(anlassAnzeige(body.data.occasion));
        setAdresse({
          company: body.data.company ?? "",
          name: body.data.name ?? "",
          street: body.data.addressStreet ?? "",
          addition: body.data.addressAddition ?? "",
          zip: body.data.addressZip ?? "",
          city: body.data.addressCity ?? "",
          countryCode: body.data.addressCountryCode || "DE",
        });
        setKundenreferenz(body.data.kundenreferenz ?? "");
        setPhase("ready");
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPhase("error");
      }
    })();
  }, [token]);

  function onAdresseFeldChange(feld: AdresseFeld, wert: string) {
    setAdresse((prev) => ({ ...prev, [feld]: wert }));
  }

  const complete =
    ((adresse.name ?? "").trim() !== "" || (adresse.company ?? "").trim() !== "") &&
    (adresse.street ?? "").trim() !== "" &&
    (adresse.zip ?? "").trim() !== "" &&
    (adresse.city ?? "").trim() !== "";

  async function submit() {
    if (!token || !complete) return;
    setPhase("submitting");
    setError(null);
    try {
      const res = await fetch(`/api/public/rechnungsadresse/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: adresse.name?.trim() || undefined,
          company: adresse.company?.trim() || undefined,
          addressStreet: adresse.street?.trim(),
          addressZip: adresse.zip?.trim(),
          addressCity: adresse.city?.trim(),
          addressAddition: adresse.addition?.trim() ?? "",
          addressCountryCode: adresse.countryCode || undefined,
          kundenreferenz: kundenreferenz.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error(t.saveError);
      setPhase("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("ready");
    }
  }

  return (
    <GastRahmen>
      {phase === "loading" && <p className="text-center font-body-sm text-body-sm text-secondary">{t.loading}</p>}
      {phase === "error" && error && (
        <p className="rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{error}</p>
      )}
      {phase === "done" && (
        <div className="text-center">
          <Icon name="check_circle" className="mx-auto mb-3 text-[40px] text-tertiary dark:text-tertiary-fixed-dim" />
          <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">{t.doneTitle}</h1>
          <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {t.doneText}
          </p>
        </div>
      )}
      {(phase === "ready" || phase === "submitting") && (
        <>
          <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">{t.heading}</h1>
          <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {occasion ? t.introEvent(occasion) : t.introInvoice}
            {t.introRest}
          </p>
          <div className="mt-6">
            <AdresseFelder
              werte={adresse}
              onFeldChange={onAdresseFeldChange}
              firmaName="gast"
              variante="platzhalter"
              laender={lang === "en" ? LAENDER_EN : LAENDER}
              labels={t.labels}
              testIdPrefix="rechnungsadresse"
            />
            <Input
              className="mt-3 w-full"
              placeholder={t.refPlaceholder}
              value={kundenreferenz}
              onChange={(e) => setKundenreferenz(e.target.value)}
            />
          </div>
          {error && (
            <p className="mt-4 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{error}</p>
          )}
          <Button
            type="button"
            className="mt-6 w-full !py-4"
            disabled={!complete || phase === "submitting"}
            onClick={() => void submit()}
          >
            {phase === "submitting" ? t.saving : t.save}
          </Button>
        </>
      )}
    </GastRahmen>
  );
}
